import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Input, Typography } from 'antd'
import { GlobalOutlined } from '@ant-design/icons'
import type { PlatformPackage } from '@/types'
import {
  isBackendHistoricalPackage,
  isPlatformPackageUploaded,
  platformLabel,
} from '../platform'
import MultiLangModal from './MultiLangModal'

export default function ReleaseNotesSection({
  changelog,
  supported_languages,
  default_language,
  editable,
  onChange,
  platform_packages,
}: {
  changelog: Record<string, string>
  supported_languages: string[]
  default_language: string
  editable: boolean
  onChange: (next: Record<string, string>) => void
  /** 分平台包形态时传入，用来提示更新日志对应哪些新传平台 */
  platform_packages?: PlatformPackage[]
}) {
  const { t } = useTranslation()
  const [modalOpen, setModalOpen] = useState(false)

  const hasOtherLang = supported_languages
    .filter((l) => l !== default_language)
    .some((l) => (changelog[l] ?? '').trim())

  const updated = (platform_packages ?? [])
    .filter(isPlatformPackageUploaded)
    .map((p) => platformLabel(p.platform))
  const reused = (platform_packages ?? [])
    .filter(isBackendHistoricalPackage)
    .map((p) => `${platformLabel(p.platform)} v${p.package_version}`)

  return (
    <div>
      {updated.length > 0 && reused.length > 0 && (
        <Typography.Text
          type="secondary"
          style={{ display: 'block', marginBottom: 8, fontSize: 12 }}
        >
          {t('versionForm.changelogPlatformsHint', {
            updated: updated.join(' / '),
            reused: reused.join(' / '),
          })}
        </Typography.Text>
      )}
      <div style={{ position: 'relative' }}>
        <Input.TextArea
          rows={3}
          maxLength={1000}
          showCount
          readOnly={!editable}
          placeholder={t('versionForm.changelogPlaceholder')}
          value={changelog[default_language] ?? ''}
          onChange={(e) =>
            onChange({ ...changelog, [default_language]: e.target.value })
          }
          style={{
            color: 'rgba(0,0,0,0.88)',
            cursor: editable ? 'text' : 'default',
          }}
        />
        {editable && (
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            title={t('versionForm.multiLangTitle')}
            style={{
              position: 'absolute',
              right: 8,
              top: 8,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              color: hasOtherLang ? 'var(--color-brand-indigo)' : 'rgba(0,0,0,0.3)',
              fontSize: 16,
              zIndex: 2,
            }}
          >
            <GlobalOutlined />
          </button>
        )}
      </div>

      <MultiLangModal
        open={modalOpen}
        value={changelog}
        supported_languages={supported_languages}
        default_language={default_language}
        onOk={(next) => {
          onChange(next)
          setModalOpen(false)
        }}
        onCancel={() => setModalOpen(false)}
      />
    </div>
  )
}
